import { Component, signal, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ContactService } from '../../../core/services/contact.service';
import { Contact } from '../../../core/models/models';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-contact-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatPaginatorModule, MatSortModule, MatIconModule, MatSnackBarModule, ConfirmDialogComponent, RouterLink],
  templateUrl: `contact-list.component.html`,
  styleUrls: [`contact-list.component.css`]
})
export class ContactListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  private contactService = inject(ContactService);
  private router = inject(Router);
  Math = Math;

  contacts = signal<Contact[]>([]);
  totalElements = signal(0);
  totalPages = signal(0);
  page = signal(0);
  size = signal(10);
  searchQuery = '';
  sortField = 'nom';
  sortDirection = 'asc';
  showArchived = signal(false);

  private searchSubject = new Subject<string>();

  showDeleteModal = signal(false);
  contactToDelete = signal<Contact | null>(null);

  constructor(private snackBar: MatSnackBar) {}

  ngOnInit() {
    this.loadContacts();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadContacts();
    });
  }

  loadContacts() {
    const sortParam = `${this.sortField},${this.sortDirection}`;
    this.contactService.getContacts(this.searchQuery, this.page(), this.size(), sortParam, this.showArchived())
      .subscribe(response => {
        this.contacts.set(response.content);
        this.totalElements.set(response.totalElements);
        this.totalPages.set(response.totalPages);
      });
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  onToggleArchived() {
    this.showArchived.update(v => !v);
    this.page.set(0);
    this.loadContacts();
  }

  onRowClick(contact: Contact) {
    if (contact.id) {
      this.router.navigate(['/contacts/edit', contact.id]);
    }
  }

  onSort(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.loadContacts();
  }
  onSortChange(sortState: Sort) {
    this.sortField = sortState.active;
    this.sortDirection = sortState.direction || 'asc';
    this.page.set(0);
    this.loadContacts();
  }

  onPageChange(event: PageEvent) {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadContacts();
  }

  getSortIcon(field: string) {
    if (this.sortField !== field) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  onDelete(contact: Contact) {
    this.contactToDelete.set(contact);
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    const contact = this.contactToDelete();
    if (contact && contact.id) {
      this.contactService.deleteContact(contact.id).subscribe({
        next: () => {
          this.loadContacts();
          this.cancelDelete();
        },
        error: (err) => {
          this.snackBar.open('Erreur lors de la suppression du contact.', 'Fermer', { duration: 3000 });
          this.cancelDelete();
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
    this.contactToDelete.set(null);
  }
}
